import {
  EasyPricingState,
  LoadingState,
  ProcessingState,
} from '@usf/ngrx-pricing';
import { EasyPricingStatus } from '../models/easy-pricing-status.model';

const createStatus = (config: EasyPricingStatus): EasyPricingStatus => ({
  status: config.status,
  cssClass: config.cssClass,
  showInfo: config.showInfo,
  tooltip: config.tooltip,
});

const getEmptyStatus = (): EasyPricingStatus =>
  createStatus({
    status: '',
    cssClass: '',
    showInfo: false,
    tooltip: '',
  });

const getCustomUpdateStatus = (
  vm: EasyPricingState,
): EasyPricingStatus | null => {
  if (!vm.isCustomUpdate) {
    return null;
  }

  if (vm.processingState === ProcessingState.processing) {
    return createStatus({
      status: 'PROCESSING',
      cssClass: 'processing',
      showInfo: false,
      tooltip: '',
    });
  }

  if (!vm.isManualUpdate) {
    if (vm.processingState === ProcessingState.success) {
      return createStatus({
        status: 'ACCEPTED',
        cssClass: 'accepted',
        showInfo: false,
        tooltip: '',
      });
    }

    if (vm.processingState === ProcessingState.error) {
      switch (vm.unifiedResponseCode) {
        case 400:
          return createStatus({
            status: 'DENIED',
            cssClass: 'denied',
            showInfo: true,
            tooltip: 'cprDeniedBelowHardFloorTooltip',
          });
        case 401:
          return createStatus({
            status: 'DENIED',
            cssClass: 'denied',
            showInfo: true,
            tooltip: 'cprDeniedAboveHardFloorTooltip',
          });
        default:
          return createStatus({
            status: 'FAILED',
            cssClass: 'failed',
            showInfo: false,
            tooltip: '',
          });
      }
    }
  }

  return null;
};

const getManualCprStatus = (vm: EasyPricingState): EasyPricingStatus | null => {
  if (
    !vm.manualCpr ||
    vm.loadingState === LoadingState.loading ||
    vm.processingState === ProcessingState.processing
  ) {
    return null;
  }

  const approvalStatus = vm.manualCpr.approvalStatus.toLowerCase();

  switch (approvalStatus) {
    case 'pending':
      return createStatus({
        status: 'PENDING',
        cssClass: 'pending',
        showInfo: false,
        tooltip: '',
      });
    case 'approved':
      if (vm.unifiedResponseCode === 0 || !vm.unifiedResponseCode) {
        return createStatus({
          status: 'APPROVED',
          cssClass: 'accepted',
          showInfo: false,
          tooltip: '',
        });
      }
      break;
    case 'rejected':
      if (vm.unifiedResponseCode === 0 || !vm.unifiedResponseCode) {
        return createStatus({
          status: 'REJECTED',
          cssClass: 'denied',
          showInfo: false,
          tooltip: '',
        });
      }
      break;
    case 'modified':
      if (vm.unifiedResponseCode === 0 || !vm.unifiedResponseCode) {
        return createStatus({
          status: 'MODIFIED',
          cssClass: 'processing',
          showInfo: false,
          tooltip: '',
        });
      }
      break;
  }

  return null;
};

export function getEasyPricingStatus(vm: EasyPricingState): EasyPricingStatus {
  if (!vm.isCustomUpdate && !vm.manualCpr) {
    return getEmptyStatus();
  }

  const customUpdateStatus = getCustomUpdateStatus(vm);
  if (customUpdateStatus) {
    return customUpdateStatus;
  }

  const manualCprStatus = getManualCprStatus(vm);
  if (manualCprStatus) {
    return manualCprStatus;
  }

  return getEmptyStatus();
}
